import { useState, type ReactElement } from 'react';
import { App, Button, Collapse, Select } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@govard/i18n-core/react';
import { ContactTaskList } from './ContactTaskList.js';
import { ContactTaskHistory } from './ContactTaskHistory.js';
import type { EntityRelatedContext } from '../../../components/entity-detail/related-context.js';
import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import {
  getTasksQueryKey,
  updateTask,
  type TaskItem,
  type TaskStatus,
} from '../../tasks/api/tasks.client.js';
import { useTasks } from '../../tasks/hooks/useTasks.js';
import { useDeleteTaskMutation } from '../../tasks/hooks/useTaskMutations.js';
import { TaskFormModal } from '../../tasks/components/TaskFormModal.js';
import { formatPersonName } from '../../../shared/format/index.js';
import { STATUS_ORDER } from '../../tasks/lib/taskMeta.js';
import { TASK_COLUMN_LABEL } from '../../tasks/hooks/useTaskGridProfile.js';

export function ContactTasksTab({ contactId }: { contactId: string }): ReactElement {
  return <EntityTasksTab contactId={contactId} />;
}

export function EntityTasksTab({
  contactId,
  dealId,
  companyId,
}: EntityRelatedContext): ReactElement {
  const { t, locale } = useTranslation();
  const { notification, message } = App.useApp();
  const scope = useCurrentScope();
  const cache = useQueryClient();
  const filters = dealId ? { dealId } : companyId ? { companyId } : { contactId: contactId! };
  const query = useTasks(filters);
  const queryKey = getTasksQueryKey(scope?.workspaceId ?? 'anonymous', filters);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<TaskItem | null>(null);
  const [history, setHistory] = useState<TaskItem | null>(null);
  const [sort, setSort] = useState('due');
  const [direction, setDirection] = useState<'ascend' | 'descend'>('ascend');
  const remove = useDeleteTaskMutation();
  const status = useMutation({
    mutationFn: ({ id, next }: { id: string; next: TaskStatus }) =>
      updateTask(id, { status: next }),
    onMutate: async ({ id, next }) => {
      await cache.cancelQueries({ queryKey });
      const previous = cache.getQueryData<TaskItem[]>(queryKey)?.find((task) => task.id === id);
      cache.setQueryData<TaskItem[]>(queryKey, (tasks) =>
        tasks?.map((task) => (task.id === id ? { ...task, status: next } : task)),
      );
      return { previous };
    },
    onError: (_error, { id }, context) => {
      cache.setQueryData<TaskItem[]>(queryKey, (tasks) =>
        tasks?.map((task) => (task.id === id && context?.previous ? context.previous : task)),
      );
      void message.error(t('crm.entity_detail.save_error'));
    },
    onSettled: async () => {
      await Promise.all(
        ['tasks', 'activities'].map((root) =>
          cache.invalidateQueries({ queryKey: [root, scope?.workspaceId ?? 'anonymous'] }),
        ),
      );
    },
  });
  const toggle = async (task: TaskItem): Promise<void> => {
    const next = task.status === 'done' ? 'todo' : 'done';
    try {
      await status.mutateAsync({ id: task.id, next });
      if (next === 'done')
        notification.success({
          title: t('crm.entity_detail.task_completed'),
          description: task.title,
          key: task.id,
          duration: 8,
          actions: (
            <Button
              onClick={() => {
                notification.destroy(task.id);
                status.mutate({ id: task.id, next: task.status });
              }}
            >
              {t('crm.entity_detail.undo')}
            </Button>
          ),
        });
    } catch {
      /* The mutation rolls back and reports the error. */
    }
  };
  const listProps = {
    ...(dealId ? { currentDealId: dealId } : {}),
    ...(companyId ? { currentCompanyId: companyId } : {}),
    pending: status.isPending,
    onToggle: (task: TaskItem) => {
      void toggle(task);
    },
    onStatus: (id: string, next: TaskStatus) => status.mutate({ id, next }),
    onEdit: (task: TaskItem) => {
      setEditing(task);
      setFormOpen(true);
    },
    onHistory: setHistory,
    onDelete: (id: string) => remove.mutateAsync(id),
    deleting: remove.isPending,
  };
  const priorities = { urgent: 0, high: 1, medium: 2, low: 3 };
  const tasks = [...(query.data ?? [])].sort((a, b) => {
    if (sort === 'original') return 0;
    const label = (task: TaskItem): string => {
      if (sort === 'assignee') return task.assigneeMember?.user.name ?? '';
      if (sort === 'deal') return task.deal?.title ?? task.inheritedLinks?.deal?.label ?? '';
      if (sort === 'contact')
        return task.contact
          ? formatPersonName(task.contact.firstName, task.contact.lastName)
          : (task.inheritedLinks?.contact?.label ?? '');
      return task.title;
    };
    const order =
      (sort === 'priority'
        ? priorities[a.priority] - priorities[b.priority]
        : sort === 'status'
          ? STATUS_ORDER[a.status] - STATUS_ORDER[b.status]
          : sort === 'createdAt'
            ? Date.parse(a.createdAt) - Date.parse(b.createdAt)
            : sort === 'due'
              ? (a.dueAt ? Date.parse(a.dueAt) : Infinity) -
                (b.dueAt ? Date.parse(b.dueAt) : Infinity)
              : label(a).localeCompare(label(b), locale)) || a.title.localeCompare(b.title, locale);
    return companyId && direction === 'descend' ? -order : order;
  });
  const completed = tasks.filter((task) => task.status === 'done' || task.status === 'cancelled');
  return (
    <>
      <ContactTaskList
        {...listProps}
        tasks={tasks.filter((task) => task.status !== 'done' && task.status !== 'cancelled')}
        loading={query.isLoading}
        error={query.error}
        filter={
          <div
            className={
              companyId
                ? 'contact-deals-filter company-detail-related-sort'
                : 'contact-deals-filter'
            }
          >
            <Select
              aria-label={t('crm.entity_detail.sort')}
              size="small"
              value={sort}
              onChange={setSort}
              style={{ minWidth: 150 }}
              options={[
                ...['due', 'priority', 'title'].map((value) => ({
                  value,
                  label: t(`crm.entity_detail.sort_${value}`),
                })),
                ...(companyId
                  ? [
                      ...(['status', 'assignee', 'deal', 'contact', 'createdAt'] as const).map(
                        (value) => ({
                          value,
                          label: TASK_COLUMN_LABEL[value],
                        }),
                      ),
                      { value: 'original', label: t('crm.features.company_detail.sort_original') },
                    ]
                  : []),
              ]}
            />
            {companyId ? (
              <Select
                size="small"
                aria-label={t('crm.features.company_detail.sort_direction')}
                value={direction}
                onChange={setDirection}
                disabled={sort === 'original'}
                style={{ minWidth: 160 }}
                options={['ascend', 'descend'].map((value) => ({
                  value,
                  label: t(`crm.features.company_detail.sort_${value}`),
                }))}
              />
            ) : null}
          </div>
        }
        action={
          <Button
            size="small"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            {t('crm.entity_detail.add_task')}
          </Button>
        }
      />
      {completed.length ? (
        <Collapse
          ghost
          items={[
            {
              key: 'completed',
              label: t('crm.entity_detail.completed_tasks', { count: completed.length }),
              children: <ContactTaskList {...listProps} tasks={completed} />,
            },
          ]}
        />
      ) : null}
      {history ? <ContactTaskHistory task={history} onClose={() => setHistory(null)} /> : null}
      {formOpen ? (
        <TaskFormModal
          open
          task={editing}
          {...(dealId
            ? { lockedDealId: dealId }
            : companyId
              ? { lockedCompanyId: companyId }
              : { lockedContactId: contactId! })}
          onClose={() => setFormOpen(false)}
        />
      ) : null}
    </>
  );
}
